import apiClient from './api';
import { RepoVersionsData } from '@app-types/types';

export const getRepoVersions = async (): Promise<RepoVersionsData> => {
  const response = await apiClient.get<RepoVersionsData>('/api/repo-versions');
  return response.data;
};

export const addRepoVersion = async (url: string): Promise<RepoVersionsData> => {
  const response = await apiClient.post<RepoVersionsData>('/api/repo-versions', { url });
  return response.data;
};

export const removeRepoVersion = async (repoId: string): Promise<void> => {
  await apiClient.delete(`/api/repo-versions/${encodeURIComponent(repoId)}`);
};

export interface RepoVersionInfo {
  version: string | null;
  dockerfileVersion: string | null;
  dockerfileEnvKey: string | null;
}

export const getRepoVersionInfo = async (
  repoId: string,
): Promise<RepoVersionInfo> => {
  const response = await apiClient.get<RepoVersionInfo>(
    `/api/repo-versions/${encodeURIComponent(repoId)}/version`,
  );
  return response.data;
};

export interface JiraTicketInfo {
  key: string;
  summary: string;
  url: string;
  status: string;
}

export const getJiraTicket = async (repoId: string): Promise<JiraTicketInfo> => {
  const response = await apiClient.get<JiraTicketInfo>(
    `/api/repo-versions/${encodeURIComponent(repoId)}/jira-ticket`,
  );
  return response.data;
};

export const linkJiraTicket = async (repoId: string, ticket: string): Promise<JiraTicketInfo> => {
  const response = await apiClient.post<JiraTicketInfo>(
    `/api/repo-versions/${encodeURIComponent(repoId)}/jira-ticket`,
    { ticket },
  );
  return response.data;
};

export interface JenkinsBuildInfo {
  success: boolean;
  jobName: string;
  jobUrl: string;
}

export const triggerJenkinsBuild = async (repoId: string): Promise<JenkinsBuildInfo> => {
  const response = await apiClient.post<JenkinsBuildInfo>(
    `/api/repo-versions/${encodeURIComponent(repoId)}/jenkins-build`,
  );
  return response.data;
};

export interface SaasBranchInfo {
  branch: string;
  pomVersion: string;
  pluginName: string;
}

export interface PlannedStep {
  id: string;
  description: string;
  method: string;
  url: string;
  body: object;
  skip?: boolean;
  skipReason?: string;
}

export interface SaasBranchPlan {
  branchName: string;
  pomVersion: string;
  pluginName: string;
  steps: PlannedStep[];
}

export const planSaasBranch = async (repoId: string, jiraKey: string): Promise<SaasBranchPlan> => {
  const response = await apiClient.post<SaasBranchPlan>(
    '/api/repo-versions/create-saas-branch',
    { repoId, jiraKey, mode: 'plan' },
  );
  return response.data;
};

export const executeSaasBranchStep = async (
  repoId: string,
  jiraKey: string,
  stepId: string,
): Promise<{ success: boolean; skipped?: boolean; alreadyExists?: boolean }> => {
  const response = await apiClient.post(
    '/api/repo-versions/create-saas-branch',
    { repoId, jiraKey, mode: 'execute', stepId },
  );
  return response.data;
};

export const createSaasBranch = async (repoId: string, jiraKey: string): Promise<SaasBranchInfo> => {
  const response = await apiClient.post<SaasBranchInfo>(
    '/api/repo-versions/create-saas-branch',
    { repoId, jiraKey },
  );
  return response.data;
};

export interface SaasPrInfo {
  number: number;
  url: string;
  title: string;
}

export const createSaasPr = async (branch: string): Promise<SaasPrInfo> => {
  const response = await apiClient.post<SaasPrInfo>(
    '/api/repo-versions/create-saas-pr',
    { branch },
  );
  return response.data;
};

export interface OpenAmBranchPlan {
  branchName: string;
  pomVersion: string;
  pluginName: string;
  forkRepo: string;
  steps: PlannedStep[];
}

export const planOpenAmBranch = async (repoId: string, branchName: string): Promise<OpenAmBranchPlan> => {
  const response = await apiClient.post<OpenAmBranchPlan>(
    '/api/repo-versions/create-openam-branch',
    { repoId, branchName, mode: 'plan' },
  );
  return response.data;
};

export const executeOpenAmBranchStep = async (
  repoId: string,
  branchName: string,
  stepId: string,
): Promise<{ success: boolean; skipped?: boolean; alreadyExists?: boolean }> => {
  const response = await apiClient.post(
    '/api/repo-versions/create-openam-branch',
    { repoId, branchName, mode: 'execute', stepId },
  );
  return response.data;
};

export const createOpenAmPr = async (branch: string): Promise<SaasPrInfo> => {
  const response = await apiClient.post<SaasPrInfo>(
    '/api/repo-versions/create-openam-pr',
    { branch },
  );
  return response.data;
};

export interface EnvironmentPlan {
  branchName: string;
  envName: string;
  steps: PlannedStep[];
}

export const planEnvironment = async (branchName: string): Promise<EnvironmentPlan> => {
  const response = await apiClient.post<EnvironmentPlan>(
    '/api/repo-versions/create-environment',
    { branchName, mode: 'plan' },
  );
  return response.data;
};

export const executeEnvironmentStep = async (
  branchName: string,
  stepId: string,
): Promise<{ success: boolean; data?: unknown }> => {
  const response = await apiClient.post(
    '/api/repo-versions/create-environment',
    { branchName, mode: 'execute', stepId },
  );
  return response.data;
};

export const generatePluginFile = async (repoId: string): Promise<void> => {
  try {
    const response = await apiClient.get(
      `/api/repo-versions/${encodeURIComponent(repoId)}/plugin`,
      { responseType: 'blob' },
    );

    // Extract filename from Content-Disposition header, fallback to plugin-config.zip
    const disposition = response.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^";\s]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : 'plugin-config.zip';

    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err: any) {
    const status = err?.response?.status;
    let extractedMessage: string | null = null;

    const responseData = err?.response?.data;
    if (responseData instanceof Blob) {
      try {
        const text = await responseData.text();
        try {
          const parsed = JSON.parse(text);
          const message = parsed?.message || parsed?.error;
          if (typeof message === 'string' && message.trim()) {
            extractedMessage = message.trim();
          }
        } catch {
          // Some proxies return plain text/html bodies on errors.
          if (typeof text === 'string' && text.trim()) {
            extractedMessage = text.trim().slice(0, 300);
          }
        }
      } catch {
        // Fall through to default error handling below.
      }
    }

    const message =
      extractedMessage
      || err?.response?.data?.message
      || err?.response?.data?.error
      || err?.message
      || 'Unknown server error while generating config';
    const suffix = status ? ` (HTTP ${status})` : '';
    throw new Error(`Generate Config failed: ${message}${suffix}`);
  }
};

export const generateTestJourney = async (environmentUrl: string, repoId: string): Promise<{ success: boolean; journeyName: string; nodeTypes?: string[] }> => {
  const response = await apiClient.post<{ success: boolean; journeyName: string; nodeTypes?: string[] }>(
    '/api/repo-versions/generate-journey',
    { environmentUrl, repoId },
  );
  return response.data;
};

export const testJourney = async (repoId: string): Promise<{ success: boolean; journeyName: string; status: string }> => {
  const response = await apiClient.post<{ success: boolean; journeyName: string; status: string }>(
    '/api/repo-versions/test-journey',
    { repoId },
    { timeout: 600000 },
  );
  return response.data;
};

export interface ScreenshotInfo {
  filename: string;
  timestamp: number;
  dataUrl: string;
}

export const getJourneyScreenshots = async (repoId: string): Promise<{ journeyName: string; screenshots: ScreenshotInfo[] }> => {
  const response = await apiClient.get<{ journeyName: string; screenshots: ScreenshotInfo[] }>(
    `/api/repo-versions/${encodeURIComponent(repoId)}/screenshots`,
  );
  return response.data;
};

export interface PipelineState {
  [repoId: string]: {
    pipelineSteps?: Record<string, { label: string; status: string }[]>;
    linkedPrs?: Record<string, string>;
    trackedPrIds?: Record<string, string>;
    linkedEnvs?: Record<string, string>;
    jiraTickets?: Record<string, JiraTicketInfo>;
  };
}

export const getPipelineState = async (): Promise<PipelineState> => {
  const response = await apiClient.get<PipelineState>('/api/repo-versions/pipeline-state');
  return response.data;
};

export const savePipelineState = async (state: PipelineState): Promise<void> => {
  await apiClient.put('/api/repo-versions/pipeline-state', state);
};

export const resetPipelineState = async (repoId?: string): Promise<void> => {
  const url = repoId
    ? `/api/repo-versions/pipeline-state/${encodeURIComponent(repoId)}`
    : '/api/repo-versions/pipeline-state/';
  await apiClient.delete(url);
};
